import React from "react";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import CodeIcon from "@mui/icons-material/Code";
import SmartButtonIcon from "@mui/icons-material/SmartButton";
import ArticleIcon from "@mui/icons-material/Article";
import QuizIcon from "@mui/icons-material/Quiz";
import ImageIcon from "@mui/icons-material/Image";
import LaunchIcon from "@mui/icons-material/Launch";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LinkIcon from "@mui/icons-material/Link";
import AppsIcon from "@mui/icons-material/Apps";
import FeedIcon from "@mui/icons-material/Feed";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import HandshakeIcon from "@mui/icons-material/Handshake";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import GridViewIcon from "@mui/icons-material/GridView";
import SmartDisplayIcon from "@mui/icons-material/SmartDisplay";
import BookmarksIcon from "@mui/icons-material/Bookmarks";
import DvrIcon from "@mui/icons-material/Dvr";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SellIcon from "@mui/icons-material/Sell";
import ArchiveIcon from "@mui/icons-material/Archive";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import type { TFunction } from "i18next";
import type { PlatformBlockType } from "../types";

export interface BlockTypeConfig {
  type: PlatformBlockType;
  /** Ключ перевода подписи типа блока (`platformBlocks.types.*.label`) */
  labelKey: string;
  /** Ключ перевода пояснения (`platformBlocks.types.*.description`) */
  descriptionKey?: string;
  icon: React.ReactElement;
  category: string;
  defaultProps?: Record<string, unknown>;
  /**
   * Витрина не рендерит этот тип блока (см. docs/assistant/knowledge/partner/
   * 19-page-blocks-reference.md, раздел «Blocks that are not rendered»):
   * зритель видит заглушку «not yet implemented». Такие типы не попадают в
   * диалог добавления блока (`blockTypes`/`contentBlockTypes` там их не
   * перечисляют), но конфиг остаётся в `blockTypesConfig`, чтобы у уже
   * существующих на странице блоков этого типа были иконка, подпись и
   * подсказка «не отображается на витрине» в списке блоков и в форме
   * редактирования.
   */
  notRendered?: boolean;
}

/** Пункт вида (карусель/сетка) — только viewType, без type */
interface ViewTypeItem {
  viewType: "CAROUSEL" | "GRID";
  labelKey: string;
  icon: React.ReactElement;
}

/** Первый массив: карусель, сетка и обычные блоки (как в Sprinty types) */
export const blockTypes: (ViewTypeItem | BlockTypeConfig)[] = [
  {
    viewType: "CAROUSEL",
    labelKey: "platformBlocks.view.CAROUSEL",
    icon: React.createElement(ViewCarouselIcon),
  },
  {
    viewType: "GRID",
    labelKey: "platformBlocks.view.GRID",
    icon: React.createElement(GridViewIcon),
  },
  {
    type: "TEXT",
    labelKey: "platformBlocks.types.TEXT.label",
    descriptionKey: "platformBlocks.types.TEXT.description",
    icon: React.createElement(TextFieldsIcon),
    category: "content",
    defaultProps: {
      variant: "body1",
      color: "textPrimary",
      align: "left",
    },
  },
  {
    type: "HTML",
    labelKey: "platformBlocks.types.HTML.label",
    icon: React.createElement(CodeIcon),
    category: "content",
  },
  {
    type: "BUTTON",
    labelKey: "platformBlocks.types.BUTTON.label",
    icon: React.createElement(SmartButtonIcon),
    category: "interactive",
    defaultProps: {
      color: "primary",
      variant: "contained",
      size: "medium",
      align: "left",
    },
  },
  {
    type: "MARKDOWN",
    labelKey: "platformBlocks.types.MARKDOWN.label",
    icon: React.createElement(ArticleIcon),
    category: "content",
  },
  {
    type: "FAQ",
    labelKey: "platformBlocks.types.FAQ.label",
    icon: React.createElement(QuizIcon),
    category: "interactive",
    defaultProps: { questions: [] },
  },
  {
    type: "BANNER",
    labelKey: "platformBlocks.types.BANNER.label",
    icon: React.createElement(ImageIcon),
    category: "media",
  },
  {
    type: "REDIRECT",
    labelKey: "platformBlocks.types.REDIRECT.label",
    icon: React.createElement(LaunchIcon),
    category: "special",
  },
  {
    type: "FAST_LINKS_LIST",
    labelKey: "platformBlocks.types.FAST_LINKS_LIST.label",
    icon: React.createElement(LinkIcon),
    category: "interactive",
  },
  {
    type: "PARTNERS",
    labelKey: "platformBlocks.types.PARTNERS.label",
    descriptionKey: "platformBlocks.types.PARTNERS.description",
    icon: React.createElement(HandshakeIcon),
    category: "media",
    defaultProps: { view: "CAROUSEL", logos: [] },
  },
  {
    // Ближайший сосед по смыслу это CONTENTS (тоже каталог видео), но, в
    // отличие от него, у ARCHIVE нет вида карусель/сетка (витрина всегда
    // рисует сетку), поэтому блок идёт в этот список с прямым выбором,
    // без шага выбора вида из contentBlockTypes.
    type: "ARCHIVE",
    labelKey: "platformBlocks.types.ARCHIVE.label",
    descriptionKey: "platformBlocks.types.ARCHIVE.description",
    icon: React.createElement(ArchiveIcon),
    category: "content",
    defaultProps: {
      itemsPerRow: 4,
      previewMode: "HORIZONTAL",
      sort: "LATEST",
    },
  },
  {
    type: "CUSTOM_COVER",
    labelKey: "platformBlocks.types.CUSTOM_COVER.label",
    icon: React.createElement(WallpaperIcon),
    category: "media",
  },
];

/** Второй массив: контентные блоки для карусели/сетки (как в Sprinty gridCarouselTypes) */
export const contentBlockTypes: BlockTypeConfig[] = [
  {
    type: "POSTS",
    labelKey: "platformBlocks.types.POSTS.label",
    icon: React.createElement(FeedIcon),
    category: "content",
    defaultProps: { view: "GRID", itemsPerRow: 3, loadMore: true },
  },
  {
    type: "CONTENTS",
    labelKey: "platformBlocks.types.CONTENTS.label",
    icon: React.createElement(SmartDisplayIcon),
    category: "content",
    defaultProps: {
      view: "GRID",
      itemsPerRow: 4,
      limit: 12,
      previewMode: "HORIZONTAL",
      sort: "LATEST",
      loadMore: true,
    },
  },
  {
    type: "PRODUCTS",
    labelKey: "platformBlocks.types.PRODUCTS.label",
    icon: React.createElement(ShoppingCartIcon),
    category: "content",
    defaultProps: {
      view: "GRID",
      itemsPerRow: 3,
      limit: 12,
      loadMore: true,
    },
  },
  {
    type: "FAVORITE_CONTENTS",
    labelKey: "platformBlocks.types.FAVORITE_CONTENTS.label",
    descriptionKey: "platformBlocks.types.FAVORITE_CONTENTS.description",
    icon: React.createElement(BookmarksIcon),
    category: "content",
    defaultProps: {
      view: "CAROUSEL",
      itemsPerRow: 4,
      limit: 12,
      previewMode: "HORIZONTAL",
    },
  },
  {
    type: "CONTINUE_WATCHING_CONTENTS",
    labelKey: "platformBlocks.types.CONTINUE_WATCHING_CONTENTS.label",
    descriptionKey:
      "platformBlocks.types.CONTINUE_WATCHING_CONTENTS.description",
    icon: React.createElement(DvrIcon),
    category: "content",
    defaultProps: {
      view: "CAROUSEL",
      itemsPerRow: 4,
      limit: 12,
      previewMode: "HORIZONTAL",
    },
  },
  {
    type: "ITEMS_LIST",
    labelKey: "platformBlocks.types.ITEMS_LIST.label",
    icon: React.createElement(FormatListBulletedIcon),
    category: "content",
    defaultProps: { view: "GRID", itemsPerRow: 3 },
  },
  {
    type: "TAGS",
    labelKey: "platformBlocks.types.TAGS.label",
    descriptionKey: "platformBlocks.types.TAGS.description",
    icon: React.createElement(SellIcon),
    category: "content",
    defaultProps: {
      view: "CAROUSEL",
      previewMode: "VERTICAL",
      itemsPerRow: 6,
      limit: 12,
      sort: "TITLE",
    },
  },
];

/**
 * Типы блоков, которые витрина не рендерит (см. `BlockTypeConfig.notRendered`
 * и docs/assistant/knowledge/partner/19-page-blocks-reference.md). Не
 * показываются в диалоге добавления блока (не входят в `blockTypes` /
 * `contentBlockTypes`), но нужны в `blockTypesConfig`, чтобы блок такого
 * типа, уже сохранённый на странице (например, импортом или до этого
 * ограничения), сохранял иконку, подпись и подсказку в списке блоков и в
 * форме редактирования, и оставался редактируемым/удаляемым.
 */
const notRenderedBlockTypes: BlockTypeConfig[] = [
  {
    type: "TAG_CARD",
    labelKey: "platformBlocks.types.TAG_CARD.label",
    icon: React.createElement(LocalOfferIcon),
    category: "content",
    defaultProps: { maxWidth: "md" },
    notRendered: true,
  },
  {
    type: "APPS_LIST",
    labelKey: "platformBlocks.types.APPS_LIST.label",
    icon: React.createElement(AppsIcon),
    category: "interactive",
    notRendered: true,
  },
  {
    type: "SERVICES",
    labelKey: "platformBlocks.types.SERVICES.label",
    icon: React.createElement(MiscellaneousServicesIcon),
    category: "content",
    notRendered: true,
  },
  {
    type: "PACKAGES",
    labelKey: "platformBlocks.types.PACKAGES.label",
    icon: React.createElement(Inventory2Icon),
    category: "content",
    notRendered: true,
  },
];

/** Все типы блоков для getBlockTypeConfig / getDefaultBlockProps */
export const blockTypesConfig: BlockTypeConfig[] = [
  ...blockTypes.filter((t): t is BlockTypeConfig => "type" in t),
  ...contentBlockTypes,
  ...notRenderedBlockTypes,
];

export const getBlockTypeConfig = (
  blockType: PlatformBlockType
): BlockTypeConfig | undefined =>
  blockTypesConfig.find((c) => c.type === blockType);

export const getDefaultBlockProps = (
  blockType: PlatformBlockType
): Record<string, unknown> => getBlockTypeConfig(blockType)?.defaultProps ?? {};

/**
 * Заголовки блока «Контент» (CONTENTS) по виду отображения и типу
 * содержимого — как в референсе (partner: getBlockSubtitle в
 * PageForm/utils.js, ключи blocks.carousel.video / blocks.grid.video и
 * т.д.): заголовок карточки блока в конструкторе и шапка формы
 * редактирования должны показывать не просто тип компонента («Видео»), а
 * вид + тип содержимого (видео/стрима/коллекций).
 */
const CONTENTS_VIEWS = ["CAROUSEL", "GRID", "LIST"];

const CONTENTS_TYPES = ["VIDEO", "STREAM", "COLLECTION"];

/**
 * Заголовок блока для карточки в списке компонентов конструктора и для
 * шапки формы редактирования. По умолчанию — подпись типа из
 * blockTypesConfig; для CONTENTS — вид + тип содержимого
 * (`platformBlocks.contentsLabel.<view>.<type>`).
 */
export const getBlockLabel = (
  block: {
    component: PlatformBlockType;
    props?: Record<string, unknown> | null;
  },
  t: TFunction
): string => {
  if (block.component === "CONTENTS") {
    const view = block.props?.view;
    const type = (block.props?.type as string | undefined) ?? "VIDEO";
    if (typeof view === "string" && CONTENTS_VIEWS.includes(view)) {
      const contentType = CONTENTS_TYPES.includes(type) ? type : "VIDEO";
      return t(`platformBlocks.contentsLabel.${view}.${contentType}`);
    }
  }

  const config = getBlockTypeConfig(block.component);

  return config ? t(config.labelKey) : block.component;
};
